import React, { useState } from "react";
import { Calendar, ChevronDown, X } from "lucide-react";

interface DateRangeFilterProps {
  selectedDate?: string;
  selectedDateFrom?: string;
  selectedDateTo?: string;
  onDateChange?: (date: string) => void;
  onDateRangeChange?: (dateFrom: string, dateTo: string) => void;
  onClear?: () => void;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  selectedDate,
  selectedDateFrom,
  selectedDateTo,
  onDateChange,
  onDateRangeChange,
  onClear,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filterMode, setFilterMode] = useState<"single" | "range">("single");
  const [tempDateFrom, setTempDateFrom] = useState(selectedDateFrom || "");
  const [tempDateTo, setTempDateTo] = useState(selectedDateTo || "");
  const [tempSingleDate, setTempSingleDate] = useState(
    selectedDate || new Date().toISOString().split("T")[0],
  );

  const getDisplayText = () => {
    if (selectedDate) {
      return new Date(selectedDate).toLocaleDateString("en-US", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }

    if (selectedDateFrom && selectedDateTo) {
      const fromDate = new Date(selectedDateFrom).toLocaleDateString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
      const toDate = new Date(selectedDateTo).toLocaleDateString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
      return `${fromDate} - ${toDate}`;
    }

    return "Select Date";
  };

  const handleApply = () => {
    if (filterMode === "single" && tempSingleDate) {
      onDateChange?.(tempSingleDate);
    } else if (filterMode === "range" && tempDateFrom && tempDateTo) {
      onDateRangeChange?.(tempDateFrom, tempDateTo);
    }
    setShowDropdown(false);
  };

  const handleClear = () => {
    setTempSingleDate(new Date().toISOString().split("T")[0]);
    setTempDateFrom("");
    setTempDateTo("");
    onClear?.();
    setShowDropdown(false);
  };

  const hasActiveFilter = Boolean(
    selectedDate || (selectedDateFrom && selectedDateTo),
  );

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center bg-slate-700 rounded px-4 py-2 text-white border border-slate-600 hover:bg-slate-600 transition-colors min-w-[200px]"
      >
        <Calendar className="w-4 h-4 mr-2 text-slate-400" />
        <span className="truncate flex-1 text-left">{getDisplayText()}</span>
        {hasActiveFilter && (
          <X
            className="w-4 h-4 ml-2 text-slate-400 hover:text-white flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          />
        )}
        <ChevronDown className="w-4 h-4 ml-2 text-slate-400 flex-shrink-0" />
      </button>

      {showDropdown && (
        <div className="absolute top-full left-0 mt-1 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-50 min-w-[300px]">
          {/* Filter Mode Toggle */}
          <div className="p-3 border-b border-slate-600">
            <div className="flex gap-2">
              <button
                onClick={() => setFilterMode("single")}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  filterMode === "single"
                    ? "bg-blue-600 text-white"
                    : "bg-slate-600 text-slate-300 hover:bg-slate-500"
                }`}
              >
                Single Date
              </button>
              <button
                onClick={() => setFilterMode("range")}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  filterMode === "range"
                    ? "bg-blue-600 text-white"
                    : "bg-slate-600 text-slate-300 hover:bg-slate-500"
                }`}
              >
                Date Range
              </button>
            </div>
          </div>

          {/* Date Inputs */}
          <div className="p-3">
            {filterMode === "single" ? (
              <div>
                <label className="block text-xs text-slate-400 mb-2">
                  Select Date
                </label>
                <input
                  type="date"
                  value={tempSingleDate}
                  onChange={(e) => setTempSingleDate(e.target.value)}
                  className="w-full bg-slate-600 border border-slate-500 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-2">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={tempDateFrom}
                    onChange={(e) => setTempDateFrom(e.target.value)}
                    className="w-full bg-slate-600 border border-slate-500 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-2">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={tempDateTo}
                    onChange={(e) => setTempDateTo(e.target.value)}
                    min={tempDateFrom || undefined}
                    className="w-full bg-slate-600 border border-slate-500 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="p-3 border-t border-slate-600 flex gap-2 justify-end">
            <button
              onClick={handleClear}
              className="px-3 py-1 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              disabled={
                filterMode === "single"
                  ? !tempSingleDate
                  : !tempDateFrom || !tempDateTo
              }
              className="px-4 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};
